import React, { useState, useMemo, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    Divider,
    CircularProgress,
} from '@mui/material';
import {
    FilterList as FilterListIcon,
    Clear as ClearIcon,
} from '@mui/icons-material';
import useFetch from '../../../../../hooks/useFetch';
import RTL from '../../../../../utils/RTL';
import { toPersianDigits } from '../../../../../utils/convertNumbers';
import { API } from '../../../../../utils/apiRoutes';
import StandardTable from '../../../../common/table/StandardTable';
import StatusChip from '../../../../common/StatusChip';
import { operationStatusTone } from '../../../../common/statusTones';
import PersianDatePicker from '../../../../common/PersianDatePicker';
import SearchableAutocomplete from '../../../../SearchableAutocomplete';
import TablePagination from '../../UserList/TablePagination';

const STATUS_CHOICES = [
    { value: 1, label: 'ثبت نام' },
    { value: 2, label: 'حاضر' },
    { value: 3, label: 'غائب' },
    { value: 4, label: 'مرخصی' },
    { value: 5, label: 'کنسل' },
    { value: 6, label: 'موارد ویژه' },
];

const PAYMENT_METHOD_CHOICES = [
    { value: 1, label: 'نقدی' },
    { value: 2, label: 'کارت خوان' },
    { value: 3, label: 'انتقال به حساب' },
    { value: 4, label: 'سایر' },
];

function OperationBrowser() {
    const [filters, setFilters] = useState({
        status_int: '',
        payment_method: '',
        teacher_id: '',
        course_id: '',
        student_id: '',
        start_date: '',
        end_date: '',
    });
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(15);

    // Fetch courses for dropdown
    const { data: coursesData } = useFetch('/operations/api/v1/teachers-and-courses/');
    const courses = coursesData?.results || [];

    // Build API params
    const apiParams = useMemo(() => {
        const params = {
            page: page + 1, // API uses 1-based pagination
            page_size: rowsPerPage,
        };
        if (filters.status_int) params.status_int = filters.status_int;
        if (filters.payment_method) params.payment_method = filters.payment_method;
        if (filters.teacher_id) params.teacher_id = filters.teacher_id;
        if (filters.course_id) params.course_id = filters.course_id;
        if (filters.student_id) params.student_id = filters.student_id;
        if (filters.start_date) {
            const dateStr = filters.start_date instanceof Date 
                ? filters.start_date.toISOString().split('T')[0] 
                : filters.start_date;
            params.start_date = dateStr;
        }
        if (filters.end_date) {
            const dateStr = filters.end_date instanceof Date 
                ? filters.end_date.toISOString().split('T')[0] 
                : filters.end_date;
            params.end_date = dateStr;
        }
        return params;
    }, [filters, page, rowsPerPage]);

    const { data, loading, error } = useFetch(API.operationBrowser, { params: apiParams });

    const operations = data?.results || data || [];
    const totalCount = data?.count || operations.length;
    const hasActiveFilters = Object.values(filters).some(v => v !== '');

    // Reset page to 0 when filters change
    useEffect(() => {
        setPage(0);
    }, [filters.status_int, filters.payment_method, filters.teacher_id, filters.course_id, filters.student_id, filters.start_date, filters.end_date]);

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
    };

    const handleClearFilters = () => {
        setFilters({
            status_int: '',
            payment_method: '',
            teacher_id: '',
            course_id: '',
            student_id: '',
            start_date: '',
            end_date: '',
        });
    };

    // Pagination handlers
    const handleChangePage = (newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0); // Reset to first page when changing rows per page
    };

    const formatCurrency = (amount) => {
        return toPersianDigits(new Intl.NumberFormat('fa-IR').format(amount || 0));
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            return toPersianDigits(date.toLocaleDateString('fa-IR'));
        } catch {
            return dateString;
        }
    };

    const formatTime = (dateString) => {
        if (!dateString) return '';
        try {
            const date = new Date(dateString);
            return toPersianDigits(date.toLocaleTimeString('fa-IR', { hour: '2-digit', minute: '2-digit' }));
        } catch {
            return '';
        }
    };

    const columns = [
        {
            id: 'date_belonged',
            label: 'تاریخ\nعملیات',
            render: (op) => (
                <Typography variant="body2">
                    {formatDate(op.date_belonged)}
                </Typography>
            )
        },
        {
            id: 'operation_date',
            label: 'تاریخ\nثبت',
            render: (op) => (
                <Typography variant="body2">
                    {formatDate(op.operation_date)}
                </Typography>
            )
        },
        {
            id: 'student',
            label: 'هنرجو',
            align: 'start',
            render: (op) => (
                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                    {op.student?.full_name || '-'}
                </Typography>
            )
        },
        {
            id: 'course',
            label: 'دوره',
            align: 'start',
            priority: 'secondary',
            render: (op) => (
                <Typography variant="body2">
                    {op.course?.course_name || '-'}
                </Typography>
            )
        },
        {
            id: 'teacher',
            label: 'آموزگار',
            align: 'start',
            priority: 'secondary',
            render: (op) => (
                <Typography variant="body2">
                    {op.teacher?.full_name || '-'}
                </Typography>
            )
        },
        {
            id: 'status',
            label: 'وضعیت',
            compact: true,
            render: (op) => (
                <StatusChip label={op.status || '-'} tone={operationStatusTone(op.status)} />
            )
        },
        {
            id: 'payment',
            label: 'روش\nپرداخت',
            priority: 'secondary',
            render: (op) => (
                <Typography variant="body2">
                    {op.payment_method_display || '-'}
                </Typography>
            )
        },
        {
            id: 'institute_portion',
            label: 'سهم\nآموزشگاه',
            render: (op) => (
                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                    {formatCurrency(op.institute_portion)} تومان
                </Typography>
            )
        },
        {
            id: 'teacher_wage',
            label: 'حق\nالزحمه',
            priority: 'secondary',
            render: (op) => (
                <Typography variant="body2">
                    {formatCurrency(op.teacher_wage)} تومان
                </Typography>
            )
        },
        {
            id: 'remained_sessions',
            label: 'جلسات\nباقی‌مانده',
            priority: 'secondary',
            render: (op) => (
                <Typography variant="body2">
                    {toPersianDigits(op.remained_sessions || 0)}
                </Typography>
            )
        },
    ];

    // Surface the fields the old detail popup had that aren't columns: the exact
    // operation time, and the note. Always rendered so the expander reliably
    // shows them (note falls back to a dash when empty).
    const renderRowDetail = (op) => (
        <>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    زمان عملیات:
                </Typography>
                <Typography variant="body2">{formatTime(op.date_belonged) || '—'}</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    زمان ثبت:
                </Typography>
                <Typography variant="body2">{formatTime(op.operation_date) || '—'}</Typography>
            </Box>
            <Box sx={{ gridColumn: '1 / -1', display: 'flex', gap: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    یادداشت:
                </Typography>
                <Typography variant="body2">{op.comment || '—'}</Typography>
            </Box>
        </>
    );

    return (
        <RTL>
            <Box sx={{ p: 3 }}>
                <Typography variant="h4" sx={{ mb: 3, textAlign: 'center', fontWeight: 'bold' }}>
                    مرور عملیات‌ها
                </Typography>

                {/* Filters */}
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <FilterListIcon sx={{ mr: 1 }} />
                        <Typography variant="h6">فیلترها</Typography>
                        {hasActiveFilters && (
                            <Button
                                size="small"
                                startIcon={<ClearIcon />}
                                onClick={handleClearFilters}
                                sx={{ mr: 'auto' }}
                            >
                                پاک کردن فیلترها
                            </Button>
                        )}
                    </Box>
                    <Divider sx={{ mb: 2 }} />

                    <Grid 
                        container 
                        spacing={0}
                        sx={{
                            '& .MuiTextField-root': { m: 0 },
                            '& .MuiFormControl-root': { m: 0 },
                        }}
                    >
                        <Grid item xs={12} sm={6} md={3} sx={{ px: 2, pb: 2 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel>وضعیت</InputLabel>
                                <Select
                                    value={filters.status_int}
                                    label="وضعیت"
                                    onChange={(e) => handleFilterChange('status_int', e.target.value || '')}
                                >
                                    <MenuItem value="">همه</MenuItem>
                                    {STATUS_CHOICES.map((choice) => (
                                        <MenuItem key={choice.value} value={choice.value}>
                                            {choice.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3} sx={{ px: 2, pb: 2 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel>روش پرداخت</InputLabel>
                                <Select
                                    value={filters.payment_method}
                                    label="روش پرداخت"
                                    onChange={(e) => handleFilterChange('payment_method', e.target.value || '')}
                                >
                                    <MenuItem value="">همه</MenuItem>
                                    {PAYMENT_METHOD_CHOICES.map((choice) => (
                                        <MenuItem key={choice.value} value={choice.value}>
                                            {choice.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3} sx={{ px: 2, pb: 2 }}>
                            <SearchableAutocomplete
                                apiUrl="/accounts/api/v1/profile/teachers/"
                                getOptionLabel={(option) => 
                                    `${option.user?.first_name || ''} ${option.user?.last_name || ''}`.trim() || option.user?.email || ''
                                }
                                getOptionValue={(option) => option?.user?.id}
                                value={filters.teacher_id || null}
                                onChange={(value) => handleFilterChange('teacher_id', value || '')}
                                label="آموزگار"
                                size="small"
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} md={3} sx={{ px: 2, pb: 2 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel>دوره</InputLabel>
                                <Select
                                    value={filters.course_id}
                                    label="دوره"
                                    onChange={(e) => handleFilterChange('course_id', e.target.value || '')}
                                >
                                    <MenuItem value="">همه</MenuItem>
                                    {courses.map((course) => (
                                        <MenuItem key={course.id} value={course.id}>
                                            {course.course_name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3} sx={{ px: 2, pb: 2 }}>
                            <SearchableAutocomplete
                                apiUrl="/accounts/api/v1/profile/students/"
                                getOptionLabel={(option) => 
                                    `${option.user?.first_name || ''} ${option.user?.last_name || ''}`.trim() || option.user?.email || ''
                                }
                                getOptionValue={(option) => option?.user?.id}
                                value={filters.student_id || null}
                                onChange={(value) => handleFilterChange('student_id', value || '')}
                                label="هنرجو"
                                size="small"
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} md={3} sx={{ px: 2, pb: 2 }}>
                            <PersianDatePicker
                                label="از تاریخ"
                                value={filters.start_date ? new Date(filters.start_date) : null}
                                onChange={(date) => {
                                    if (date && !isNaN(date.getTime())) {
                                        handleFilterChange('start_date', date.toISOString().split('T')[0]);
                                    } else {
                                        handleFilterChange('start_date', '');
                                    }
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} md={3} sx={{ px: 2, pb: 2 }}>
                            <PersianDatePicker
                                label="تا تاریخ"
                                value={filters.end_date ? new Date(filters.end_date) : null}
                                onChange={(date) => {
                                    if (date && !isNaN(date.getTime())) {
                                        handleFilterChange('end_date', date.toISOString().split('T')[0]);
                                    } else {
                                        handleFilterChange('end_date', '');
                                    }
                                }}
                            />
                        </Grid>
                    </Grid>
                </Paper>

                {/* Loading State */}
                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 8 }}>
                        <CircularProgress />
                    </Box>
                )}

                {/* Results Count */}
                {!loading && (
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                            تعداد نتایج: {toPersianDigits(totalCount)}
                        </Typography>
                    </Box>
                )}

                {/* Operations Table */}
                {!loading && (
                    <StandardTable
                        columns={columns}
                        rows={operations}
                        loading={false}
                        error={error}
                        renderRowDetail={renderRowDetail}
                        emptyText="عملیاتی یافت نشد."
                        ariaLabel="جدول عملیات‌های مالی"
                    />
                )}

                {/* Pagination */}
                {!loading && totalCount > 0 && (
                    <TablePagination
                        totalCount={totalCount}
                        page={page}
                        rowsPerPage={rowsPerPage}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                )}
            </Box>
        </RTL>
    );
}

export default OperationBrowser;

